import { clearActivationContinuation, readPendingOnboarding, savePendingOnboarding } from 'services/auth/onboardingStatus';
import { hasOrganizationPermission, ORGANIZATION_PERMISSIONS } from './organizationPermissions';
import {
  getCurrentMockUser,
  getMockCommercialContext,
  getSelectedOrganizationId,
  listCurrentUserInvitations,
  listCurrentUserOrganizationViews,
  readMockOrganizationStore,
  replaceCurrentUserOrganizations,
  saveMockAccessRequest,
  saveMockInvitation,
  saveMockOrganizationStore,
  setSelectedOrganizationId,
  upsertOrganizationForCurrentUser,
} from './mockOrganizationStore';

const INVITATION_ROLE_IDS = {
  'Organization Administrator': '0f418ead-0b68-4af4-b141-0b894503f559',
  'Billing Admin': 'mock-role-billing-admin',
  Reviewer: '5febd304-7990-4ddd-a9ce-18812aae2a4f',
  Submitter: '4b03b020-194f-45a9-810a-06801ffa513c',
  Approver: '801e1d35-a9b5-4ef3-972d-c005cf503243',
};

const createMockIdSuffix = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
const isOrganizationAdministrator = (membership) =>
  String(membership?.role || '').trim().toLowerCase() === 'organization administrator';

const ACTIVE_WORKSPACE_KEY = 'workplansiq.activeWorkspace';
const LEGACY_SELECTED_TENANT_KEY = 'selectedTenant';
const DEMO_MEMBERSHIPS_KEY = 'workplansiq.demoOrganizations';

const migrateLegacyWorkspace = (storage) => {
  const current = storage.getItem(ACTIVE_WORKSPACE_KEY);
  if (current) return current;

  const legacy = storage.getItem(LEGACY_SELECTED_TENANT_KEY);
  if (!legacy) return null;

  storage.setItem(ACTIVE_WORKSPACE_KEY, legacy);
  storage.removeItem(LEGACY_SELECTED_TENANT_KEY);
  return legacy;
};

// Run the storage-key migration as soon as this module is loaded, including on
// the login page before a workspace is selected.
if (typeof window !== 'undefined') {
  migrateLegacyWorkspace(window.sessionStorage);
  migrateLegacyWorkspace(window.localStorage);
}

const readLegacyStoredTenant = () => {
  try {
    return JSON.parse(migrateLegacyWorkspace(sessionStorage))
      || JSON.parse(migrateLegacyWorkspace(localStorage))
      || null;
  } catch {
    return null;
  }
};

const MOCK_TENANT_MEMBERSHIPS = [
  {
    id: 'cerdas-inovasi',
    name: 'PT Cerdas Inovasi',
    identifier: 'cerdasinovasi.worksplaniq.com',
    plan: 'Professional',
    role: 'Organization Administrator',
    roleId: '0f418ead-0b68-4af4-b141-0b894503f559',
    status: 'active',
    orgId: 'mock-org-cerdas-inovasi',
    wcId: 'mock-wc-cerdas-inovasi',
  },
  {
    id: 'maju-bersama',
    name: 'PT Maju Bersama',
    identifier: 'majubersama.worksplaniq.com',
    plan: 'Enterprise',
    role: 'Reviewer',
    roleId: '5febd304-7990-4ddd-a9ce-18812aae2a4f',
    status: 'active',
    orgId: 'mock-org-maju-bersama',
    wcId: 'mock-wc-maju-bersama',
  },
  {
    id: 'solusi-prima',
    name: 'PT Solusi Prima',
    identifier: 'solusiprima.worksplaniq.com',
    plan: 'Starter',
    role: 'Submitter',
    roleId: '4b03b020-194f-45a9-810a-06801ffa513c',
    status: 'active',
    orgId: 'mock-org-solusi-prima',
    wcId: 'mock-wc-solusi-prima',
  },
  {
    id: 'mandiri-jaya',
    name: 'PT Mandiri Jaya',
    identifier: 'mandirijaya.worksplaniq.com',
    plan: 'Starter',
    role: 'Approver',
    roleId: '801e1d35-a9b5-4ef3-972d-c005cf503243',
    status: 'pending',
    orgId: 'mock-org-mandiri-jaya',
    wcId: 'mock-wc-mandiri-jaya',
  },
  {
    id: 'inovasi-digital',
    name: 'PT Inovasi Digital',
    identifier: 'inovasidigital.worksplaniq.com',
    plan: 'Professional',
    role: 'Reviewer',
    roleId: '5febd304-7990-4ddd-a9ce-18812aae2a4f',
    status: 'suspended',
    orgId: 'mock-org-inovasi-digital',
    wcId: 'mock-wc-inovasi-digital',
  },
];

export const getTenantMemberships = async () => {
  const normalizedMemberships = listCurrentUserOrganizationViews();
  if (normalizedMemberships.length) return Promise.resolve(normalizedMemberships);

  try {
    const demoMemberships = JSON.parse(sessionStorage.getItem(DEMO_MEMBERSHIPS_KEY));
    if (Array.isArray(demoMemberships) && demoMemberships.length) {
      return Promise.resolve(replaceCurrentUserOrganizations(demoMemberships));
    }
  } catch { /* Continue with memberships from the normal source. */ }

  const onboardingTenant = readLegacyStoredTenant();

  if (onboardingTenant?.source === 'onboarding-mock') {
    const onboarding = readPendingOnboarding();
    const signedInEmail = sessionStorage.getItem('workplansiq.signedInEmail')?.trim().toLowerCase();
    const workspaceEmail = onboardingTenant.ownerEmail?.trim().toLowerCase()
      || onboarding?.email?.trim().toLowerCase();
    const belongsToSignedInUser = Boolean(signedInEmail && workspaceEmail === signedInEmail);
    const workspaceIsReady = onboarding?.onboardingStatus === 'WORKSPACE_READY';

    if (belongsToSignedInUser && workspaceIsReady) {
      return Promise.resolve(replaceCurrentUserOrganizations([onboardingTenant]));
    }
  }

  // Production must not grant mock workspace memberships to every account.
  // Enable this explicitly only when reviewing the tenant-selection UI locally.
  const mockEnabled =
    process.env.REACT_APP_USE_MOCK_TENANT_MEMBERSHIPS === 'true';

  return Promise.resolve(mockEnabled
    ? replaceCurrentUserOrganizations(MOCK_TENANT_MEMBERSHIPS)
    : []);
};

export const saveDemoTenantMemberships = (memberships) => {
  sessionStorage.setItem(DEMO_MEMBERSHIPS_KEY, JSON.stringify(memberships));
  replaceCurrentUserOrganizations(memberships);
};

export const getSelectedTenant = () => {
  const selectedOrganizationId = getSelectedOrganizationId();
  if (!selectedOrganizationId) return null;

  return listCurrentUserOrganizationViews()
    .find((item) => item.id === selectedOrganizationId) || null;
};

const persistSelectedTenant = (tenantView) => {
  setSelectedOrganizationId(tenantView.id);
  sessionStorage.removeItem(ACTIVE_WORKSPACE_KEY);
  sessionStorage.removeItem(LEGACY_SELECTED_TENANT_KEY);
  localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
  localStorage.removeItem(LEGACY_SELECTED_TENANT_KEY);
  sessionStorage.setItem('orgId', tenantView.orgId);
  sessionStorage.setItem('wcId', tenantView.wcId);
  try {
    const demoMemberships = JSON.parse(sessionStorage.getItem(DEMO_MEMBERSHIPS_KEY));
    if (Array.isArray(demoMemberships) && demoMemberships.some((item) => item.id === tenantView.id)) {
      sessionStorage.setItem(DEMO_MEMBERSHIPS_KEY, JSON.stringify(
        demoMemberships.map((item) => item.id === tenantView.id ? tenantView : item),
      ));
    }
  } catch { /* Demo membership synchronization is best effort only. */ }
  return tenantView;
};

export const saveSelectedTenant = (tenant) => {
  const membership = listCurrentUserOrganizationViews().find((item) => item.id === tenant?.id);
  if (!membership) throw new Error('Cannot select an organization without an active membership.');

  const changed = (field) =>
    JSON.stringify(tenant?.[field] ?? null) !== JSON.stringify(membership?.[field] ?? null);
  const requiredPermissions = new Set();
  if (['plan', 'planCode', 'planCapacity', 'planDurationMonths'].some(changed)) {
    requiredPermissions.add(ORGANIZATION_PERMISSIONS.PLAN_MANAGE);
  }
  if (changed('billingAccount') || ['invoiceNumber', 'invoiceIssuedAt', 'paymentDueAt', 'temporaryAccessEndAt'].some(changed)) {
    requiredPermissions.add(ORGANIZATION_PERMISSIONS.ORDER_CREATE);
  }
  if (changed('paymentStatus')) {
    requiredPermissions.add(tenant.paymentStatus === 'PAID'
      ? ORGANIZATION_PERMISSIONS.PAYMENT_MANAGE
      : ORGANIZATION_PERMISSIONS.ORDER_CREATE);
  }
  if (changed('paidAt')) requiredPermissions.add(ORGANIZATION_PERMISSIONS.PAYMENT_MANAGE);
  if (changed('subscriptionStatus')) {
    requiredPermissions.add(tenant.subscriptionStatus === 'ACTIVE'
      ? ORGANIZATION_PERMISSIONS.PAYMENT_MANAGE
      : tenant.subscriptionStatus === 'PLAN_DRAFT'
        ? ORGANIZATION_PERMISSIONS.PLAN_MANAGE
        : ORGANIZATION_PERMISSIONS.ORDER_CREATE);
  }
  if (changed('accessStatus')) {
    requiredPermissions.add(tenant.accessStatus === 'FULL_ACCESS'
      ? ORGANIZATION_PERMISSIONS.PAYMENT_MANAGE
      : ORGANIZATION_PERMISSIONS.ORDER_CREATE);
  }
  const denied = [...requiredPermissions].some(
    (permission) => !hasOrganizationPermission(membership, permission),
  );
  if (denied) {
    throw new Error('Your organization role does not allow plan or billing changes.');
  }

  const tenantView = upsertOrganizationForCurrentUser({
    ...tenant,
    role: membership.role,
    roleId: membership.roleId,
    membershipId: membership.membershipId,
    membershipStatus: membership.membershipStatus,
    permissions: membership.permissions,
  });
  return persistSelectedTenant(tenantView);
};

export const clearSelectedTenant = () => {
  setSelectedOrganizationId(null);
  sessionStorage.removeItem(ACTIVE_WORKSPACE_KEY);
  sessionStorage.removeItem(LEGACY_SELECTED_TENANT_KEY);
  sessionStorage.removeItem('orgId');
  sessionStorage.removeItem('wcId');
  localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
  localStorage.removeItem(LEGACY_SELECTED_TENANT_KEY);
};

export const validateSelectedTenant = async () => {
  const memberships = (await getTenantMemberships()).filter(
    (membership) => !membership.membershipStatus || membership.membershipStatus === 'ACTIVE',
  );
  const selectedId = getSelectedOrganizationId();
  const selectedTenant = memberships.find((membership) => membership.id === selectedId) || null;

  if (!selectedTenant) {
    clearSelectedTenant();
    return { selectedTenant: null, memberships };
  }

  setSelectedOrganizationId(selectedTenant.id);
  sessionStorage.removeItem(ACTIVE_WORKSPACE_KEY);
  sessionStorage.setItem('orgId', selectedTenant.orgId || '');
  sessionStorage.setItem('wcId', selectedTenant.wcId || '');
  return { selectedTenant, memberships };
};

export const switchActiveOrganization = (organizationId) => {
  const membership = listCurrentUserOrganizationViews().find(
    (item) => item.id === organizationId && item.membershipStatus === 'ACTIVE',
  );
  if (!membership) {
    throw new Error('You do not have an active membership for this organization.');
  }
  if (['inactive', 'suspended'].includes(membership.status)) {
    throw new Error('This organization is currently unavailable.');
  }

  return saveSelectedTenant(membership);
};

export const createMockOrganization = ({ name, identifier, email = '' }) => {
  const timestamp = Date.now();
  const idSuffix = createMockIdSuffix();
  const baseIdentifier = String(identifier || name || 'organization')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '') || `organization-${timestamp}`;
  const existingIdentifiers = new Set(
    readMockOrganizationStore().organizations.map((item) => item.identifier),
  );
  let normalizedIdentifier = baseIdentifier;
  let suffix = 2;
  while (existingIdentifiers.has(normalizedIdentifier)) {
    normalizedIdentifier = `${baseIdentifier}-${suffix}`;
    suffix += 1;
  }
  const organizationId = `organization-${idSuffix}`;
  const organization = {
    id: organizationId,
    name: String(name || '').trim(),
    identifier: normalizedIdentifier,
    email: String(email || '').trim().toLowerCase(),
    plan: 'Plan not selected',
    status: 'active',
    setupStatus: 'PENDING',
    subscriptionStatus: 'NO_SUBSCRIPTION',
    paymentStatus: 'NOT_STARTED',
    accessStatus: 'EXPLORATION',
    workspaceSetupStatus: 'PENDING',
    workspaceSetupStep: 0,
    workspaceSetupFlowVersion: 2,
    orgId: `mock-org-${idSuffix}`,
    wcId: `mock-wc-${idSuffix}`,
    source: 'ui-demo',
    role: 'Organization Administrator',
    roleId: '0f418ead-0b68-4af4-b141-0b894503f559',
    membershipId: `membership-admin-${organizationId}`,
    membershipStatus: 'ACTIVE',
  };

  const createdOrganization = upsertOrganizationForCurrentUser(organization);
  saveSelectedTenant(createdOrganization);
  return getSelectedTenant();
};

// Stage 1 normalized mock accessors. Existing screens can keep consuming the
// joined tenant view while new flows work with the four independent entities.
export const getMockOrganizationData = () => readMockOrganizationStore();
export const getOrganizationCommercialContext = (organizationId) => getMockCommercialContext(organizationId);
export const getCurrentUserInvitations = () => listCurrentUserInvitations();
export const saveInvitation = (invitation) => saveMockInvitation(invitation);

export const createMockInvitation = ({ organizationId, inviteeEmail, role }) => {
  const inviter = getCurrentMockUser();
  const inviterMembership = listCurrentUserOrganizationViews().find(
    (item) => item.id === organizationId && isOrganizationAdministrator(item),
  );
  if (!inviterMembership) throw new Error('Only an organization administrator can invite members.');

  const timestamp = Date.now();
  return saveMockInvitation({
    invitationId: `invitation-${createMockIdSuffix()}`,
    organizationId,
    organizationName: inviterMembership.name,
    inviteeEmail,
    role,
    roleId: INVITATION_ROLE_IDS[role] || null,
    status: 'PENDING',
    invitedBy: inviter.name || inviter.email || inviter.id,
    createdAt: new Date(timestamp).toISOString(),
    expiresAt: new Date(timestamp + (7 * 24 * 60 * 60 * 1000)).toISOString(),
  });
};

export const getMockInvitation = (invitationId) => {
  const invitation = readMockOrganizationStore().invitations.find(
    (item) => item.invitationId === invitationId,
  );
  if (!invitation) return null;
  if (invitation.status === 'PENDING' && new Date(invitation.expiresAt).getTime() <= Date.now()) {
    return saveMockInvitation({ ...invitation, status: 'EXPIRED' });
  }
  return invitation;
};

export const respondToMockInvitation = (invitationId, decision) => {
  const invitation = getMockInvitation(invitationId);
  if (!invitation) throw new Error('Invitation was not found.');
  if (invitation.status !== 'PENDING') throw new Error(`This invitation is ${invitation.status.toLowerCase()}.`);

  const user = getCurrentMockUser();
  if (!user.email || user.email !== String(invitation.inviteeEmail || '').trim().toLowerCase()) {
    throw new Error('This invitation was issued to a different email address.');
  }

  if (decision === 'DECLINED') {
    return { invitation: saveMockInvitation({ ...invitation, status: 'DECLINED' }), membership: null };
  }
  if (decision !== 'ACCEPTED') throw new Error('Unsupported invitation response.');

  const organization = readMockOrganizationStore().organizations.find(
    (item) => item.id === invitation.organizationId,
  );
  if (!organization) throw new Error('The invitation organization is no longer available.');

  const membership = upsertOrganizationForCurrentUser({
    ...organization,
    role: invitation.role,
    roleId: invitation.roleId || INVITATION_ROLE_IDS[invitation.role] || null,
    membershipId: `membership-${user.id}-${organization.id}`,
    membershipStatus: 'ACTIVE',
  });
  const acceptedInvitation = saveMockInvitation({ ...invitation, status: 'ACCEPTED' });
  saveSelectedTenant(membership);
  return { invitation: acceptedInvitation, membership };
};

export const revokeMockInvitation = (invitationId) => {
  const invitation = getMockInvitation(invitationId);
  if (!invitation) throw new Error('Invitation was not found.');
  const membership = listCurrentUserOrganizationViews().find(
    (item) => item.id === invitation.organizationId && isOrganizationAdministrator(item),
  );
  if (!membership) throw new Error('Only an organization administrator can revoke invitations.');
  if (invitation.status !== 'PENDING') throw new Error(`This invitation is ${invitation.status.toLowerCase()}.`);
  return saveMockInvitation({ ...invitation, status: 'REVOKED' });
};

export const createMockAccessRequest = ({ organizationReference }) => {
  const user = getCurrentMockUser();
  if (!user.email) throw new Error('A signed-in email is required to request access.');
  const reference = String(organizationReference || '').trim().toLowerCase()
    .replace(/^https?:\/\//, '').replace(/\/$/, '');
  const store = readMockOrganizationStore();
  const organization = store.organizations.find((item) => {
    const identifier = String(item.identifier || '').toLowerCase();
    return [String(item.id || '').toLowerCase(), identifier, `${identifier}.worksplaniq.com`, String(item.email || '').toLowerCase()]
      .includes(reference);
  });
  if (!organization) throw new Error('No organization matches that code, domain, or administrator email.');

  const alreadyMember = store.memberships.some(
    (item) => item.userId === user.id && item.organizationId === organization.id && item.status === 'ACTIVE',
  );
  if (alreadyMember) throw new Error('You already have access to this organization.');
  const existingPending = store.accessRequests.find(
    (item) => item.requesterUserId === user.id && item.organizationId === organization.id && item.status === 'PENDING',
  );
  if (existingPending) return existingPending;

  const timestamp = Date.now();
  return saveMockAccessRequest({
    requestId: `access-request-${createMockIdSuffix()}`,
    organizationId: organization.id,
    organizationName: organization.name,
    requesterUserId: user.id,
    requesterEmail: user.email,
    requesterName: user.name,
    requestedRole: 'Reviewer',
    status: 'PENDING',
    createdAt: new Date(timestamp).toISOString(),
    reviewedAt: null,
    reviewedBy: null,
  });
};

export const getCurrentUserAccessRequests = () => {
  const user = getCurrentMockUser();
  return readMockOrganizationStore().accessRequests.filter(
    (item) => item.requesterUserId === user.id,
  );
};

export const getAdminAccessRequests = () => {
  const adminOrganizationIds = new Set(
    listCurrentUserOrganizationViews()
      .filter(isOrganizationAdministrator)
      .map((item) => item.id),
  );
  return readMockOrganizationStore().accessRequests.filter(
    (item) => adminOrganizationIds.has(item.organizationId),
  );
};

export const cancelMockAccessRequest = (requestId) => {
  const user = getCurrentMockUser();
  const request = readMockOrganizationStore().accessRequests.find((item) => item.requestId === requestId);
  if (!request || request.requesterUserId !== user.id) throw new Error('Access request was not found.');
  if (request.status !== 'PENDING') throw new Error(`This request is already ${request.status.toLowerCase()}.`);
  return saveMockAccessRequest({ ...request, status: 'CANCELLED' });
};

export const reviewMockAccessRequest = (requestId, decision) => {
  if (!['APPROVED', 'REJECTED'].includes(decision)) throw new Error('Unsupported access request decision.');
  const reviewer = getCurrentMockUser();
  const store = readMockOrganizationStore();
  const request = store.accessRequests.find((item) => item.requestId === requestId);
  if (!request) throw new Error('Access request was not found.');
  const reviewerMembership = store.memberships.find((item) =>
    item.userId === reviewer.id
    && item.organizationId === request.organizationId
    && item.status === 'ACTIVE'
    && isOrganizationAdministrator(item));
  if (!reviewerMembership) throw new Error('Only an organization administrator can review this request.');
  if (request.status !== 'PENDING') throw new Error(`This request is already ${request.status.toLowerCase()}.`);

  if (decision === 'APPROVED') {
    const membershipId = `membership-${request.requesterUserId}-${request.organizationId}`;
    const membership = {
      id: membershipId,
      userId: request.requesterUserId,
      organizationId: request.organizationId,
      role: request.requestedRole || 'Reviewer',
      roleId: INVITATION_ROLE_IDS[request.requestedRole || 'Reviewer'],
      status: 'ACTIVE',
    permissions: [],
    };
    store.memberships = store.memberships.some((item) => item.id === membershipId)
      ? store.memberships.map((item) => item.id === membershipId ? membership : item)
      : [...store.memberships, membership];
    saveMockOrganizationStore(store);
  }

  return saveMockAccessRequest({
    ...request,
    status: decision,
    reviewedAt: new Date().toISOString(),
    reviewedBy: reviewer.name || reviewer.email || reviewer.id,
  });
};

export const provisionTemporaryWorkspace = ({ ownerEmail, selectedPlan } = {}) => {
  const draft = readPendingOnboarding() || {};
  const plan = selectedPlan || {};
  const timestamp = Date.now();
  const identifier = `workspace-${timestamp}`;
  const hasSelectedPlan = Boolean(plan.title || plan.code || plan.id || draft.planTitle || draft.planCode);
  const invoiceIssued = Boolean(plan.invoiceId || draft.invoiceId || draft.invoiceStatus === 'PENDING');
  const temporaryAccessEndAt = invoiceIssued
    ? new Date(timestamp + (14 * 24 * 60 * 60 * 1000)).toISOString()
    : null;
  const tenant = {
    id: identifier,
    name: 'New WorkPlansIQ Organization',
    identifier,
    plan: plan.title || draft.planTitle || draft.planCode || 'Plan not selected',
    role: 'Organization Administrator',
    roleId: '0f418ead-0b68-4af4-b141-0b894503f559',
    status: invoiceIssued ? 'temporary-access' : 'active',
    subscriptionStatus: invoiceIssued ? 'INVOICE_PENDING' : hasSelectedPlan ? 'PLAN_DRAFT' : 'NO_SUBSCRIPTION',
    paymentStatus: invoiceIssued ? 'PENDING_PAYMENT' : 'NOT_STARTED',
    accessStatus: invoiceIssued ? 'TEMPORARY_ACCESS' : 'EXPLORATION',
    workspaceSetupStatus: 'PENDING',
    workspaceSetupStep: 0,
    workspaceSetupFlowVersion: 2,
    temporaryAccessEndAt,
    paymentDueAt: temporaryAccessEndAt,
    invoiceIssuedAt: invoiceIssued ? new Date(timestamp).toISOString() : null,
    orgId: `mock-org-${timestamp}`,
    wcId: `mock-wc-${timestamp}`,
    source: 'onboarding-mock',
    ownerEmail: String(ownerEmail || draft.email || '').trim().toLowerCase(),
  };

  const provisionedTenant = upsertOrganizationForCurrentUser(tenant);
  saveSelectedTenant(provisionedTenant);
  savePendingOnboarding({
    ...draft,
    planCode: draft.planCode || plan.code || plan.id,
    planTitle: draft.planTitle || plan.title,
    capacity: draft.capacity || plan.capacity,
    durationMonths: draft.durationMonths || plan.durationMonths,
    onboardingStatus: 'WORKSPACE_READY',
    workspaceCreatedAt: new Date(timestamp).toISOString(),
  });
  clearActivationContinuation();
  return tenant;
};
